import {useCallback, useContext, useEffect, useState} from "react"
import {
    Button,
    Header,
    HeaderContent,
    HeaderSubheader, Icon, Label, Placeholder, PlaceholderLine, Segment,
    Table,
    TableBody, TableCell,
    TableHeader, TableHeaderCell, TableRow
} from "semantic-ui-react";
import {InvestmentSummaryView} from "../../app/models/GpLanding/InvestmentSummaryView";
import {useStore} from "../../app/stores/store";
import {NavLink, useParams} from "react-router-dom";
import {InvestmentInterest} from "../../app/models/Investment/InvestmentInterest";
import EntityContext from "../../app/context/entityContext";
import {toast} from "react-toastify";
import {formatCurrency, formatDate, getEnumValue, getFundingColor, getRandomNumber} from "../../shared/utils";
import TransferModal from "../transfer/transferModal";
import {TransferModel, TransferType} from "../../app/models/settings/transfer";
import {CapBlockTable} from "../../app/models/common/CapblockTable";
import {FundingStatus, InvestmentFundingMethod} from "../../app/models/Investment/InvestmentInterestGrid";
import FundingTransferInfo from "./FundingTransferInfo";

function FundInvestment() {
    const {investmentId} = useParams();
    const {entity} = useContext(EntityContext);
    const [interestList, setInterestList] = useState<InvestmentInterest[]>([]);
    const [loading, setLoading] = useState(true);
    const {investmentInsightsStore} = useStore();
    const {investmentStore} = useStore();
    const [investment, setInvestment] = useState<InvestmentSummaryView>();
    const [totalPledged, setTotalPledged] = useState(0);
    const [paymentsCompleted, setPaymentsCompleted] = useState(0);
    const [showCryptoTransfer, setShowCryptoTransfer] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [cryptoModel, setCryptoModel] = useState<TransferModel>();
    const [refreshData, setRefreshData] = useState(0);
    const [interest, setInterest] = useState(new InvestmentInterest());
    const {stripeAccountStore} = useStore();

    const getData = useCallback(() => {
        setLoading(true);
        investmentStore.getInvestmentInterests(entity.id, Number(investmentId))
            .then((r) => {
                const sumInterests = (interests: InvestmentInterest[]) => interests.reduce((sum, interest) => sum + interest.amount, 0);
                const totalPledged = r.filter(interest => getEnumValue(FundingStatus, interest.fundingStatus) !== FundingStatus.funded);
                const totalFunded = r.filter(interest => getEnumValue(FundingStatus, interest.fundingStatus) === FundingStatus.funded);
                setTotalPledged(sumInterests(totalPledged));
                setPaymentsCompleted(sumInterests(totalFunded));
                setInterestList(r);
            })
            .then(() => investmentInsightsStore.getOverview(Number(investmentId))
                .then(r => {
                    setInvestment(r);
                })
            )
            .catch(r => {
                toast.error("An error occurred while retrieving investment information", {theme: 'colored'})
            })
            .finally(() => setLoading(false))
    }, [investmentId, investmentInsightsStore, investmentStore, entity.id]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        if (refreshData > 0) {
            getData();
        }
    }, [refreshData, getData]);

    const handleTransferClose = (refresh: boolean) => {
        setShowCryptoTransfer(false);
        setShowTransferModal(false);
        if (refresh) {
            setRefreshData(getRandomNumber);
        }
    }

    const handlePayment = (interest: InvestmentInterest) => {
        setInterest(interest);
        if (interest.fundingMethod === InvestmentFundingMethod.ach) {
            stripeAccountStore.getFundingPaymentUrl(interest.id)
                .then((r) => {
                    window.location.href = r;
                });
        } else if (interest.fundingMethod === InvestmentFundingMethod.crypto) {
            setCryptoData(interest);
        } else {
            setShowTransferModal(true);
        }
    }

    const setCryptoData = (interest: InvestmentInterest) => {
        setCryptoModel({
            senderCapblockTable: CapBlockTable.entity,
            senderTableKeyId: entity.id,
            transferType: TransferType.investmentFunding,
            sourceId: interest.id,
            amount: interest.amount,
            toCapblockTable: CapBlockTable.entity,
            toTableKeyId: investment!.sponsorEntityId,
            toName: investment!.sponsorName,
        });
        setShowCryptoTransfer(true);
    }

    return (
        <>
            <Header as='h1'>
                <HeaderContent>Fund Investment</HeaderContent>
                <HeaderSubheader><NavLink
                    to={`/investor/investment-insights/${Number(investmentId)}`}> {investment?.name}</NavLink></HeaderSubheader>
            </Header>
            <div>You're just one step away from finalizing your investment. Let's secure your spot!</div>
            <Header as='h2'>
                <HeaderContent>Your Pledges</HeaderContent>
            </Header>
            {loading ?

                <Segment loading>
                    <Placeholder>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                    </Placeholder>
                </Segment>

                :
                <>
                    <Table color={'green'}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Share Class</TableHeaderCell>
                                <TableHeaderCell>Number of Shares</TableHeaderCell>
                                <TableHeaderCell>$ Amount</TableHeaderCell>
                                <TableHeaderCell>Payment Method</TableHeaderCell>
                                <TableHeaderCell>Status</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {interestList.map((interest, idx) => {
                                const fundingStatus = getEnumValue(FundingStatus, interest.fundingStatus);
                                return <TableRow key={idx}>
                                    <TableCell>{interest.shareClass}</TableCell>
                                    <TableCell>{interest.shareCount}</TableCell>
                                    <TableCell>{formatCurrency(interest.amount)}</TableCell>
                                    <TableCell>{interest.fundingMethodDescription}</TableCell>
                                    <TableCell>
                                        {fundingStatus === FundingStatus.awaitingTransfer ?
                                            <Button primary onClick={() => handlePayment(interest)}>
                                                Fund Now
                                            </Button>
                                            :
                                            <Label style={{width: '75%'}}
                                                   color={getFundingColor(interest.fundingStatus)}
                                                   circular
                                                   size={'large'}>
                                                <Icon name='check circle outline'></Icon>
                                                {interest.fundingStatusDescription}</Label>
                                        }
                                    </TableCell>
                                </TableRow>
                            })}
                        </TableBody>
                    </Table>
                    <div>Click the "Fund Now" option for your pledges to complete the payment.</div>
                </>
            }

            <Header as='h2'>
                <HeaderContent>Investment Breakdown</HeaderContent>
            </Header>
            {loading ?
                <Segment loading>
                    <Placeholder>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                        <PlaceholderLine></PlaceholderLine>
                    </Placeholder>
                </Segment>
                :
                <>
                    <Table color={'green'}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Total Pledged</TableHeaderCell>
                                <TableHeaderCell>Payments Completed</TableHeaderCell>
                                <TableHeaderCell>Deadline</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell>{formatCurrency(totalPledged)}</TableCell>
                                <TableCell>{formatCurrency(paymentsCompleted)}</TableCell>
                                <TableCell>{formatDate(investment?.fundingDeadlineDate)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    {showCryptoTransfer && <TransferModal closeModal={handleTransferClose}
                                                          show={showCryptoTransfer}
                                                          hideReceive={true}
                                                          transferModel={cryptoModel}/>
                    }

                    {showTransferModal && <FundingTransferInfo
                        show={showTransferModal}
                        closeModal={handleTransferClose}
                        interest={interest}
                        investmentId={Number(investmentId)}
                    />
                    }
                </>
            }

        </> // main container
    )
}

export default FundInvestment;